import {
  Box,
  Flex,
  Heading,
  SimpleGrid,
  useMediaQuery,
} from "@chakra-ui/react";
import PendingApprovals, { PendingApprovalsProps } from "./PendingApprovals";
import React, { useEffect, useState } from "react";
import {
  STATUS_APPROVED,
  STATUS_REJECTED
} from "src/common/constants/statusConstants";

import AcceptedUsers from "./AcceptedUsers";
import AdminInfo from "./AdminInfo";
import AdminMatrimonyProfiles from "./AdminMatrimonyProfiles";
import AdminMatrimonyProfileEdit from "./AdminMatrimonyProfileEdit";
import MarriageConsultants from "./MarriageConsultants";
import { AdminService } from "src/rest/adminService";
import { Admin_Labels_Eng } from "src/common/constants/englishLabels";
import { Admin_Labels_Hindi } from "src/common/constants/hindiLabels";
import AnalyticsCard from "./AnalyticsCard";
import Charts from "./Charts";
import HeaderComponent from "src/pages/register/HeaderComponent";
import { HistoryTable } from "./HistoryTable";
import { RootState } from "src/store/store";
import Sidebar from "./Sidebar";
import { useSelector } from "react-redux";

type DashboardPageProps = {
  pendingProps?: PendingApprovalsProps;
  headerOffset?: number;
};

const DashboardPage: React.FC<DashboardPageProps> = ({
  pendingProps,
  headerOffset = 0,
}) => {
  const [active, setActive] = useState("dashboard");
  const [editProfileId, setEditProfileId] = useState<number | null>(null);
  const [isWide] = useMediaQuery("(min-width: 1000px)");

  // analytics counts
  const [acceptedCount, setAcceptedCount] = useState<number>(0);
  const [rejectedCount, setRejectedCount] = useState<number>(0);
  const pendingCount = pendingProps?.userDetailsList?.length ?? 0;
  const totalUsers = acceptedCount + pendingCount + rejectedCount;

  const globalInfo = useSelector((state: RootState) => state.globalInfo);
  const { globalLanguage } = globalInfo;
  const isHindiLabels = globalLanguage === 'HI';

  useEffect(() => {
    const loadCounts = async () => {
      try {
        const [approved, rejected] = await Promise.all([
          AdminService.getApprovedCount(),
          AdminService.getRejectedCount(),
        ]);
        setAcceptedCount(approved);
        setRejectedCount(rejected);
      } catch (e) {
        //
      }
    };
    if (active === "dashboard") loadCounts();
  }, [active]);

  useEffect(() => {
    if (!isWide) setActive((prev) => prev); // trigger re-render on breakpoint change
  }, [isWide]);

  const handleEditProfile = (profileId: number) => {
    setEditProfileId(profileId);
    setActive("editMatrimonyProfile");
  };

  const handleBackToProfiles = () => {
    setEditProfileId(null);
    setActive("matrimonyProfiles");
  };

  return (
    <Flex
      width="100%"
      height={`calc(100vh - ${headerOffset}px)`}
      align="stretch"
      overflow="hidden"
    >
      <Sidebar
        activeKey={active}
        onChange={setActive}
        onWidthChange={() => {}}
      />
      {/* Right column: header + scrollable content. Solid background to match screenshot */}
      <Box flex={1} bg="#F8F7F5" display="flex" flexDirection="column" minW={0}>
        {/* Sticky white header bar aligned with content; use original HeaderComponent */}
        <Box
          as="header"
          position="sticky"
          top={0}
          zIndex={10}
          bg="#FFFFFF"
          borderBottom="1px solid #E5E7EB"
          px={{ base: 4, md: 8 }}
          py={3}
        >
          <HeaderComponent />
        </Box>

        {/* Scrollable main area with increased left padding and spacing */}
        <Box as="main" px={{ base: 6, md: 10 }} py={8} overflow="auto">
          {active === "pending" && pendingProps ? (
            <PendingApprovals {...pendingProps} />
          ) : active === "accepted" ? (
            <AcceptedUsers status={STATUS_APPROVED} />
          ) : active === "rejected" ? (
            <AcceptedUsers status={STATUS_REJECTED} />
          ) : active === "editMatrimonyProfile" && editProfileId != null ? (
            <AdminMatrimonyProfileEdit
              profileId={editProfileId}
              onBack={handleBackToProfiles}
            />
          ) : active === "matrimonyProfiles" ? (
            <AdminMatrimonyProfiles onEditProfile={handleEditProfile} />
          ) : active === "adminInfo" ? (
            <AdminInfo />
          ) : active === "marriageConsultants" ? (
            <MarriageConsultants />
          ) : (
            <>
              <Heading size="lg" color="#4A2C2A" mb={6}>
                {isHindiLabels?Admin_Labels_Hindi.ADMIN_DASHBOARD:Admin_Labels_Eng.ADMIN_DASHBOARD}
              </Heading>
              <SimpleGrid
                columns={{ base: 1, md: 3, xl: 3 }}
                spacing={6}
                mb={10}
              >
                <AnalyticsCard
                  title={isHindiLabels? Admin_Labels_Hindi.TOTAL_REGISTERED_USERS:Admin_Labels_Eng.TOTAL_REGISTERED_USERS}
                  value={totalUsers}
                />
                <AnalyticsCard title={isHindiLabels? Admin_Labels_Hindi.PENDING_APPROVALS: Admin_Labels_Eng.PENDING_APPROVALS}value={pendingCount} />
                <AnalyticsCard title={isHindiLabels? Admin_Labels_Hindi.ACCEPTED_USERS: Admin_Labels_Eng.ACCEPTED_USERS} value={acceptedCount} />
              </SimpleGrid>

              <Box mb={10}>
                <Charts
                  approvedCount={acceptedCount}
                  pendingCount={pendingCount}
                  rejectedCount={rejectedCount}
                />
              </Box>

              <Box>
                <HistoryTable />
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Flex>
  );
};

export default DashboardPage;
